import NavBar from "../../components/common/NavBar";
import React, { useState } from "react";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { gql, useQuery } from "@apollo/client";
import AddSponsorModal from "../../components/sponsor/AddSponsorModal";
import EditSponsorModal from "../../components/sponsor/EditSponsorModal";
// import { confirmDelete } from "../../components/common/confirmDelete";
// import { toast } from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const MY_SPONSORS = gql`
    query GetSponsors($page: Int, $pageSize: Int) {
        sponsors(page: $page, pageSize: $pageSize) {
            objects {
                id
                name
                contactName
                contactEmail
                website
                primarySport
                notes
                isActive
                billingCountryCode
                billingCurrency
            }
            page
            pages
            hasPrev
            hasNext
            totalCount
        }
    }
`;

export const DELETE_SPONSOR = gql`
    mutation DeleteSponsor($id: ID!) {
        deleteSponsor(id: $id) {
            ok
            error
            deletedId
        }
    }
`;

function Sponsors() {
    const { t } = useTranslation();
    const scopeId = "kbola-sponsors-page";

    const navBarElem = {
        pageTitle: t('sponsors.advertising'),
        links: [
            { pageTitle: t('sponsors.advertising'), navLink: null, active: false },
            { pageTitle: t('sponsors.sponsors'), navLink: `/advertise/sponsor`, active: true },
        ],
    };

    const PAGE_SIZE = 10;

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sponsors, setSponsors] = useState([]);
    const [deletingId, setDeletingId] = useState(null);

    const { loading, error, refetch } = useQuery(MY_SPONSORS, {
        variables: { page, pageSize: PAGE_SIZE },
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setSponsors(data?.sponsors?.objects || []);
            setTotalPages(data?.sponsors?.pages || 1);
        },
    });

    // const [deleteSponsorMutation] = useMutation(DELETE_SPONSOR, {
    //     onError: (err) => toast.error(getLocalizedGraphQLError(err, t, 'sponsors.deleteError')),
    // });

    async function doRefetch(currentPage) {
        const result = await refetch({ page: currentPage, pageSize: PAGE_SIZE });
        const rows = result?.data?.sponsors?.objects || [];
        const pages = result?.data?.sponsors?.pages || 1;
        setSponsors(rows);
        setTotalPages(pages);
        return { rows, pages };
    }

    // function requestDelete(sponsor) {
    //     confirmDelete({
    //         title: t('sponsors.deleteSponsor'),
    //         message: t('sponsors.deleteConfirmMessage', { name: sponsor.name }),
    //         confirmLabel: t('sponsors.delete'),
    //         cancelLabel: t('sponsors.cancel'),
    //         danger: true,
    //         busy: deletingId === sponsor.id,
    //         onConfirm: async () => {
    //             try {
    //                 setDeletingId(sponsor.id);
    //
    //                 const { data } = await deleteSponsorMutation({
    //                     variables: { id: sponsor.id },
    //                 });
    //
    //                 const payload = data?.deleteSponsor;
    //                 if (!payload?.ok) {
    //                     toast.error(payload?.error || t('sponsors.deleteCouldNot'));
    //                     return;
    //                 }
    //
    //                 toast.success(t('sponsors.deleteSuccess'));
    //
    //                 // Refresh current page first
    //                 const { rows } = await doRefetch(page);
    //
    //                 // If page is now empty and not first page, step back one page
    //                 if (rows.length === 0 && page > 1) {
    //                     setPage((prev) => prev - 1);
    //                 }
    //             } finally {
    //                 setDeletingId(null);
    //             }
    //         },
    //     });
    // }

    // modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedSponsor, setSelectedSponsor] = useState(null);

    function handleOpenAdd() {
        setShowAddModal(true);
    }

    function handleOpenEdit(sponsor) {
        setSelectedSponsor(sponsor);
        setShowEditModal(true);
    }

    async function handleCreated() {
        await doRefetch(page);
    }

    async function handleUpdated() {
        await doRefetch(page);
    }

    return (
        <>
            <div className="content-wrapper" id={scopeId}>
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    {error && (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                        </div>
                    )}

                    <PaginatedFlatTable
                        title={t('sponsors.sponsors')}
                        page={page}
                        pages={totalPages}
                        loading={loading || deletingId !== null}
                        items={sponsors}
                        headers={[
                            t('sponsors.headers.name'),
                            t('sponsors.headers.contactName'),
                            t('sponsors.headers.contactEmail'),
                            t('sponsors.headers.active'),
                            t('sponsors.headers.actions'),
                        ]}
                        onPageChange={(p) => {
                            // No manual refetch here; useQuery runs automatically when page changes
                            setPage(p);
                        }}
                        onAdd={handleOpenAdd}
                        renderRow={(sponsor, index) => (
                            <tr key={sponsor.id || index}>
                                <td>
                                    <div className="font-weight-bold">{sponsor.name || "—"}</div>
                                    <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                        {sponsor.website || ""}
                                    </div>
                                    <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                        {t('sponsors.sport')}: {sponsor.primarySport || "—"}
                                    </div>
                                    <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                                        {t('sponsors.billing')}:{" "}
                                        {(sponsor.billingCurrency || "").toUpperCase() || "—"}{" "}
                                        {sponsor.billingCountryCode || "—"}
                                    </div>
                                </td>

                                <td>{sponsor.contactName || "—"}</td>
                                <td>{sponsor.contactEmail || "—"}</td>

                                <td>
                                    {sponsor.isActive ? (
                                        <span className="badge badge-success">{t('sponsors.activeBadge')}</span>
                                    ) : (
                                        <span className="badge badge-secondary">{t('sponsors.inactiveBadge')}</span>
                                    )}
                                </td>

                                <td className="text-right">
                                    <div className="kbola-row-actions">
                                        <button
                                            type="button"
                                            className="kbola-action kbola-action-edit"
                                            onClick={() => handleOpenEdit(sponsor)}
                                            title={t('sponsors.editSponsor')}
                                        >
                                            <i className="fas fa-edit" />
                                        </button>

                                        {/*<button*/}
                                        {/*    type="button"*/}
                                        {/*    className="kbola-action kbola-action-danger"*/}
                                        {/*    onClick={() => requestDelete(sponsor)}*/}
                                        {/*    disabled={deletingId === sponsor.id}*/}
                                        {/*    title={t('sponsors.deleteSponsorTitle')}*/}
                                        {/*>*/}
                                        {/*    <i className="fas fa-trash" />*/}
                                        {/*</button>*/}
                                    </div>
                                </td>
                            </tr>
                        )}
                    />
                </section>

                <style>{`
          #${scopeId} .kbola-row-actions{
            display: inline-flex;
            gap: 8px;
            align-items: center;
          }

          #${scopeId} .kbola-action{
            width: 38px;
            height: 38px;
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,0.10);
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;
            transition: transform 120ms ease, background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
          }

          #${scopeId} .kbola-action:hover{
            transform: translateY(-1px);
          }

          #${scopeId} .kbola-action:disabled{
            opacity: 0.55;
            cursor: not-allowed;
            transform: none;
          }

          #${scopeId} .kbola-action-edit{
            border: 1px solid rgba(0,0,0,0.10);
            background: rgba(0,0,0,0.02);
            color: #14151a;
          }

          #${scopeId} .kbola-action-danger{
            border: 1px solid rgba(220,53,69,0.35);
            background: rgba(220,53,69,0.06);
            color: #dc3545;
          }

          #${scopeId} button:focus{
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }

          #${scopeId} i.fas,
          #${scopeId} i.far,
          #${scopeId} i.fal,
          #${scopeId} i.fa,
          #${scopeId} i[class^="fa-"],
          #${scopeId} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          #${scopeId} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }
        `}</style>
            </div>

            <AddSponsorModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onCreated={handleCreated}
            />

            <EditSponsorModal
                show={showEditModal}
                sponsor={selectedSponsor}
                onClose={() => setShowEditModal(false)}
                onUpdated={handleUpdated}
            />
        </>
    );
}

export default Sponsors;
